import { useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@carbon/react';
import cx from 'clsx';
import { keyBy, round } from 'lodash';

import { formatArea, formatNumeric } from 'utils/format';
import styles from 'theme/features/dashboard/dashboard-business-unit.module.scss';

const ROW_TYPE = {
  primary: 0,
  secondary: 1,
  highlighted: 2
};

const BusinessUnitBreakdownTable = ({ breakdown, specialtySpaces, measurementSystem }) => {
  const specialtySpacesById = useMemo(() => keyBy(specialtySpaces, 'id'), [specialtySpaces]);

  const renderRow = ({ title, breakdown, type = ROW_TYPE.primary }) => (
    <TableRow
      key={title}
      className={cx(
        type === ROW_TYPE.secondary && styles['table__secondary-row'],
        type === ROW_TYPE.highlighted && styles['table__highlighted-row']
      )}
    >
      <TableCell>{title}</TableCell>
      <TableCell>{breakdown.seats === null ? '—' : formatNumeric(breakdown.seats)}</TableCell>
      <TableCell>{formatArea(breakdown.area, measurementSystem)}</TableCell>
      <TableCell>{formatNumeric(breakdown.workplacePercentage)}%</TableCell>
      <TableCell>{formatNumeric(round(breakdown.overallPercentage, 1))}%</TableCell>
    </TableRow>
  );

  const renderRowBy = ({ title, breakdownBy, type = ROW_TYPE.primary }) =>
    renderRow({
      title,
      breakdown: breakdown[breakdownBy],
      type
    });

  return (
    <Table size="sm" className={styles.table}>
      <TableHead>
        <TableRow>
          <TableHeader />
          <TableHeader>Seats No.</TableHeader>
          <TableHeader>Area</TableHeader>
          <TableHeader>Percentage of Workplace</TableHeader>
          <TableHeader>Percentage Overall</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {renderRowBy({ title: 'Collaborative', breakdownBy: 'collaborative' })}
        {renderRowBy({ title: 'Individual', breakdownBy: 'individual' })}
        {renderRowBy({ title: 'Workstations', breakdownBy: 'individualWorkstations', type: ROW_TYPE.secondary })}
        {renderRowBy({ title: 'Additional Spaces', breakdownBy: 'individualOther', type: ROW_TYPE.secondary })}
        {renderRowBy({ title: 'Community + Support', breakdownBy: 'communitySupport' })}
        {renderRowBy({ title: 'BU Subtotal', breakdownBy: 'subTotal', type: ROW_TYPE.highlighted })}
        {renderRowBy({ title: 'BU Specialty Spaces', breakdownBy: 'specialty' })}
        {breakdown.specialtyBySpace.map((bySpace) =>
          renderRow({
            title: specialtySpacesById[bySpace.spaceId].name,
            breakdown: bySpace,
            type: ROW_TYPE.secondary
          })
        )}
        {renderRowBy({ title: 'Site-specific Spaces', breakdownBy: 'siteSpecific' })}
        {renderRowBy({ title: 'Project Total', breakdownBy: 'total', type: ROW_TYPE.highlighted })}
      </TableBody>
    </Table>
  );
};

export default BusinessUnitBreakdownTable;
